import { API } from '../constants/api';

import { publicAxios } from './ShareLinkService';

type PublicTrackingStatus = 'booked' | 'dispatched' | 'in-transit' | 'delivered';

/** One end of the route, as the result row prints it ("Chicago, IL"). */
interface IPublicTrackingPlace {
    city: string | null;
    stateCode: string | null;
    country: string | null;
}

/** A date-only milestone (YYYY-MM-DD) and whether it already happened or is still planned. */
interface IPublicTrackingMilestone {
    date: string | null;
    isActual: boolean;
}

export interface IPublicTrackingResult {
    orderId: string;
    orderStatus: PublicTrackingStatus;
    /** A live tracking share token, for every status (booked opens on its pickup schedule). */
    link: { token: string };
    /** When the lookup stops answering (7 days after delivery); null while the shipment is live. */
    expiresAt: string | null;
    /** First leg's shipper — the route as the customer thinks of it, never an internal junction. */
    origin: IPublicTrackingPlace | null;
    /** Last leg's consignee. */
    destination: IPublicTrackingPlace | null;
    pickup: IPublicTrackingMilestone | null;
    delivery: IPublicTrackingMilestone | null;
}

/** "Chicago, IL" — city alone when there is no state, null when the stop has neither. */
export const formatPlace = (place: IPublicTrackingPlace | null): string | null => {
    if (!place) return null;

    return [place.city, place.stateCode].filter(Boolean).join(', ') || null;
};

/** Same messages the backend sends, so the page can tell the outcomes apart by status code alone. */
export const PUBLIC_TRACKING_STATUS = {
    INVALID: 400,
    NOT_FOUND: 404,
    /** Delivered and past the one-week window — the only miss the backend describes (410 Gone). */
    EXPIRED: 410,
    TOO_MANY: 429,
} as const;

/**
 * Body of a 410. `deliveredAt` is a date-only string in the consignee's timezone, or null when the
 * order has no recorded delivery time — the panel then drops the date from the message rather than
 * guessing one.
 */
export interface IPublicTrackingExpired {
    orderId: string;
    deliveredAt: string | null;
}

/** Reads the 410 body off a rejected lookup. Falls back to the typed ID when the body is unusable. */
export const readExpiredDetail = (error: any, walId: string): IPublicTrackingExpired => ({
    orderId: error?.response?.data?.data?.orderId ?? walId,
    deliveredAt: error?.response?.data?.data?.deliveredAt ?? null,
});

/**
 * WAL ID format, mirrored from the backend (utils/publicTracking.ts WAL_ID_REGEX). Checked before
 * any request so a malformed ID never reaches the API (and never counts against the visitor's IP).
 */
export const WAL_ID_REGEX = /^WAL-[A-Z0-9]{4,5}-\d{1,9}$/;

export const normalizeWalId = (raw: string): string => raw.trim().toUpperCase();

// publicAxios unwraps response.data on 200, so this resolves to { message, data }.
export const lookupPublicTracking = async (walId: string): Promise<IPublicTrackingResult> => {
    const response: any = await publicAxios.get(API.PUBLIC_TRACKING.LOOKUP, { params: { walId } });

    return response?.data as IPublicTrackingResult;
};
